/* eslint-disable react/prop-types */
// We should just convert to ts eventually
/* eslint-disable no-use-before-define */
import { tocs } from '@react/search/helpers';
import { getImagePath } from '@common/helpers/images';
import { titleize } from '@common/helpers/strings';
import { getBreadCrumbsForLocation, getStateAbbr } from '@common/helpers/url';
import Helmet from 'next/head';
import { number, object, string, bool, array } from 'proptypes';
import React from 'react';
import { host } from 'sly/config';
import { getReviewerName } from 'sly/review/helpers';
import {
  getYoutubeThumbnailLink,
  generateYoutubeEmbedURL,
  generateVimeoEmbedURL,
  getVimeoThumbnailLinkFromURL
} from '@sly/core/helpers/video';
import { getSource } from './getSource';

export const stringifyReplacer = (k, v) => {
  if (k === 'hash' || k === 'key') {
    return undefined;
  }
  return v;
};

export const getSDForCommunity = ({
  name,
  url,
  address,
  propRatings = {},
  startingRate,
  images,
  communityPhone,
  reviews,
  slyScore
}) => {
  const { reviewsValue, numReviews } = propRatings || {};

  const ld = {};
  ld['@context'] = 'http://schema.org';
  ld['@type'] = 'LocalBusiness';
  ld.name = name;
  ld.url = `${host}${url}`;
  ld.telephone = communityPhone;

  if (startingRate > 0) {
    ld.priceRange = `From $${startingRate.toLocaleString()} per month`;
  }

  if (images && images.length > 0) {
    ld.image = images
      ?.filter(image => !!image.path)
      .map(image => {
        return getImagePath(encodeURI(image.path.replace(/\.jpe?g$/i, '.jpg')));
      });
  }

  const geo = {};
  geo['@type'] = 'GeoCoordinates';
  geo.latitude = address.latitude;
  geo.longitude = address.longitude;
  ld.geo = geo;

  const addressLd = {};
  addressLd['@type'] = 'PostalAddress';
  addressLd.streetAddress = address.line1;
  addressLd.addressLocality = address.city;
  addressLd.addressRegion = address.state;
  addressLd.postalCode = address.zip;
  const country = {};
  country['@type'] = 'Country';
  country.name = address.country;
  addressLd.addressCountry = country;
  ld.address = addressLd;

  if (numReviews && numReviews > 0) {
    const aggregatedRating = {};
    aggregatedRating['@type'] = 'AggregateRating';
    reviewsValue < 1
      ? (aggregatedRating.ratingValue = 1)
      : (aggregatedRating.ratingValue = reviewsValue?.toFixed(1));
    aggregatedRating.reviewCount = numReviews;
    ld.aggregateRating = aggregatedRating;
    if (reviews && reviews.length > 0) {
      ld.reviews = reviews.map(review => {
        const reviewName = getReviewerName(review);
        return {
          '@context': 'https://schema.org',
          '@type': 'Review',
          author: {
            '@type': 'Person',
            name: reviewName
          },
          datePublished: review.createdAt,
          reviewBody: review.comments,
          reviewRating: {
            '@type': 'Rating',
            worstRating: 1,
            bestRating: 5,
            ratingValue: review.value
          }
        };
      });
    }
  } else if (propRatings?.aggregateRatingStats?.numReviews && !!slyScore) {
    const aggregatedRating = {};
    aggregatedRating['@type'] = 'AggregateRating';
    aggregatedRating.ratingValue = Number((slyScore / 10.0).toFixed(1));
    aggregatedRating.ratingCount = propRatings.aggregateRatingStats?.numReviews;
    ld.aggregateRating = aggregatedRating;
    aggregatedRating.bestRating = 10;
    aggregatedRating.worstRating = 1;
  }

  return ld;
};

getSDForCommunity.propTypes = {
  name: string.isRequired,
  url: string.isRequired,
  address: object.isRequired,
  propRatings: object,
  startingRate: number,
  images: array,
  propInfo: object
};

export const getWebPageSDForCommunity = ({
  name,
  isD2C,
  partnerCollection,
  isClaimed,
  seoLastUpdatedAt,
  dohLicenseNumber,
  auditDate
}) => {
  const ld = {};
  ld['@context'] = 'http://schema.org';
  ld['@type'] = 'WebPage';
  ld.inLanguage = 'EN-US';

  const { name: reviewerName } = getSource({
    isD2C,
    partnerCollection,
    isClaimed,
    seoLastUpdatedAt,
    name,
    dohLicenseNumber,
    auditDate
  });

  if (name) {
    ld.reviewedBy = {
      '@type': 'Person',
      name: reviewerName
    };
  }
  return ld;
};

getWebPageSDForCommunity.propTypes = {
  name: string.isRequired,
  isD2C: bool,
  partnerCollection: object,
  isClaimed: bool,
  propInfo: object,
  seoLastUpdatedAt: string
};

export const questionLD = faq => {
  const question = {};
  question['@type'] = 'Question';
  question.name = faq.question;
  const answer = {};
  answer['@type'] = 'Answer';
  answer.text = faq.answer;
  question.acceptedAnswer = answer;
  return question;
};

export const author = () => {
  return {
    '@type': 'Organization',
    name: 'Seniorly Inc.'
  };
};

export const audience = () => {
  return {
    '@type': 'Audience',
    name: 'Care Givers, Seniors, Adult Children, Senior Care Providers, Senior Housing'
  };
};

export const reviewedBy = reviewer => {
  return {
    '@type': 'Person',
    name: reviewer?.info?.displayName
  };
};

export const tocSiteNavigationLD = (baseUrl, links) => {
  const ld = {};
  ld['@context'] = 'http://schema.org';
  const ldLinks = [];
  if (links?.length > 0) {
    links.map(e =>
      ldLinks.push({
        '@context': 'https://schema.org',
        '@type': 'SiteNavigationElement',
        '@id': baseUrl,
        name: e.title,
        url: `${baseUrl}#${e.id}`
      })
    );
  }
  ld['@graph'] = ldLinks;
  return (
    <script
      type='application/ld+json'
      dangerouslySetInnerHTML={{
        __html: JSON.stringify(ld, stringifyReplacer)
      }}
    ></script>
  );
};

export const guideLD = (title, description, url) => {
  const ld = {};
  ld['@context'] = 'http://schema.org';
  ld['@type'] = 'Guide';
  ld.headline = title;
  ld.description = description;
  ld.url = url;
  ld.audience = audience();
  ld.author = author();
  return (
    <script
      type='application/ld+json'
      dangerouslySetInnerHTML={{
        __html: JSON.stringify(ld, stringifyReplacer)
      }}
    ></script>
  );
};

export const faqPage = faqs => {
  const ld = {};
  ld['@context'] = 'http://schema.org';
  ld['@type'] = 'FAQPage';
  const ldFAQs = [];
  if (faqs.length > 0) {
    faqs.map(e => ldFAQs.push(questionLD(e)));
  }
  ld.mainEntity = ldFAQs;
  return (
    <script
      type='application/ld+json'
      dangerouslySetInnerHTML={{
        __html: JSON.stringify(ld, stringifyReplacer)
      }}
    ></script>
  );
};

export const generateTocList = content =>
  content
    ?.filter(({ __component }) => __component?.includes('subtitle'))
    ?.map(({ value: title, subtitleId: id }) => ({ title, id }));

export const SearchPageSeo = ({
  city,
  state,
  toc,
  county,
  location,
  latitude,
  longitude,
  communityList,
  pagination,
  strapiTitle,
  reviewer
}) => {
  let actualToc = tocs.find(elem => elem.value === toc);
  if (typeof actualToc === 'undefined') {
    actualToc = {
      label: 'Assisted Living',
      value: 'assisted-living',
      segment: 'assisted-living',
      seoLabel: 'Assisted Living'
    };
  }

  const canonicalPath = `/${toc}/${state}${
    city || county ? '/' + (city || county) : ''
  }`;

  let locationStr = '';
  if (county) {
    locationStr = `${titleize(county)}, ${getStateAbbr(state)}`;
  } else if (city) {
    locationStr = `${titleize(city)}, ${getStateAbbr(state)}`;
  } else {
    locationStr = `${titleize(state)}`;
  }

  let numResultsStr =
    pagination.count < 20 ? `The ${pagination.count} Best` : `The 20 Best`;

  if (pagination.count === 0) {
    numResultsStr = 'No Results for';
  }

  const pageText =
    pagination.current > 1 ? ` – Page ${pagination.current}` : '';

  const title = `${numResultsStr} ${actualToc.seoLabel} in ${locationStr}${pageText} | Seniorly`;
  let description = city
    ? `Get pricing & read reviews for ${numResultsStr} ${
        actualToc.seoLabel
      } in ${locationStr}. Find detailed property information, photos & talk to local ${titleize(
        city
      )} senior living experts.`
    : `${numResultsStr} ${actualToc.seoLabel} in ${locationStr}. Find detailed property information, pricing, reviews & local senior care advice for ${locationStr} ${actualToc.label} communities`;

  if (toc === 'senior-living') {
    description = `Search senior living communities in ${locationStr} that range from assisted living facilities, memory care communities and other retirement living options. Compare cost, property highlights and more.`;
  }

  if (toc === 'skilled-nursing-facility') {
    description = `Search nursing homes facilities in ${locationStr}. Learn about medicare and medicaid options, property highlights and more.`;
  }

  const url = `${location.pathname}${location.search}`;

  const ld = {};
  ld['@context'] = 'http://schema.org';
  ld['@type'] = 'WebPage';
  ld.url = `${host}${url}`;
  ld.inLanguage = 'EN-US';
  ld.author = author();
  ld.audience = audience();
  ld.name = title;
  ld.description = description;
  if (!!reviewer) {
    ld.reviewedBy = reviewedBy(reviewer);
  }

  const ldIL = {};
  ldIL['@context'] = 'http://schema.org';
  ldIL['@type'] = 'ItemList';
  ldIL.name = title;
  ldIL.description = description;
  // not setting host in this page, as it clashes with Breadcrumbs url in local. should be fine in prod, as url is relative
  ldIL.url = `${url}`;
  ldIL.itemListElement = [];
  ldIL.numberOfItems = communityList.length;

  const communityUrls = [];
  if (communityList.length > 0) {
    communityList.map((e, index) => {
      const { name, url, gallery } = e;
      communityUrls.push(`${host}${url}`);
      let imageUrl = null;
      if (gallery && gallery.images && gallery.images.length > 0) {
        const [image] = gallery.images;
        imageUrl = getImagePath(
          encodeURI(image.path.replace(/\.jpe?g$/i, '.jpg'))
        );
      }
      const ldLI = {
        '@type': 'LocalBusiness',
        url: `${url}`,
        name,
        address: e?.addressString,
        image: {
          '@type': 'ImageObject',
          name,
          url: imageUrl
        }
      };
      return ldIL.itemListElement.push(ldLI);
      // return ldCommunities.push(getSDForSearchResource({ ...e }));
    });
  }

  ld.significantLink = communityUrls.join(', ');

  const ldCity = {};
  if (city) {
    ldCity['@context'] = 'http://schema.org';
    ldCity['@type'] = 'City';
    ldCity.name = titleize(city);
    if (latitude && longitude) {
      const geo = {};
      geo['@type'] = 'GeoCoordinates';
      geo.latitude = latitude;
      geo.longitude = longitude;
      ldCity.geo = geo;
    }
  }

  const items = getBreadCrumbsForLocation(
    { toc, state, city },
    false,
    'search-breadcrumb'
  );

  const noindex =
    Object.keys(pagination.rejectParams).length ||
    pagination.count <= 3 ||
    !communityList.some(profile => !profile.noIndex);

  return (
    <Helmet>
      <title>{strapiTitle || title}</title>
      <meta name='description' content={description} />
      <meta content={description} property='og:description' />
      <meta content={strapiTitle || title} property='og:title' />

      <meta content={description} property='twitter:description' />
      <meta content={strapiTitle || title} property='twitter:title' />

      <link rel='canonical' href={`${host}${canonicalPath}`} />

      {noindex && <meta name='robots' content='noindex' />}

      <script
        type='application/ld+json'
        dangerouslySetInnerHTML={{
          __html: JSON.stringify(ld, stringifyReplacer)
        }}
      />

      {ldIL.itemListElement.length > 0 && (
        <script
          type='application/ld+json'
          dangerouslySetInnerHTML={{
            __html: JSON.stringify(ldIL, stringifyReplacer)
          }}
        />
      )}
      {city && (
        <script
          type='application/ld+json'
          dangerouslySetInnerHTML={{
            __html: JSON.stringify(ldCity, stringifyReplacer)
          }}
        />
      )}
      {breadcrumbLD(items)}
    </Helmet>
  );
};

export const breadcrumbLD = items => {
  const ld = {};
  ld['@context'] = 'http://schema.org';
  ld['@type'] = 'BreadcrumbList';
  ld['itemListElement'] = items.map((item, index) => {
    return {
      '@type': 'ListItem',
      position: index + 1,
      item: {
        '@id': `${host}${item.path}`,
        name: item.label
      }
    };
  });
  return (
    <script
      type='application/ld+json'
      dangerouslySetInnerHTML={{
        __html: JSON.stringify(ld, stringifyReplacer)
      }}
    ></script>
  );
};

export const breadcrumbLDObject = items => {
  const ld = {};
  ld['@context'] = 'http://schema.org';
  ld['@type'] = 'BreadcrumbList';
  ld['itemListElement'] = items.map((item, index) => {
    return {
      '@type': 'ListItem',
      position: index + 1,
      item: {
        '@id': `${host}${item.path}`,
        name: item.label
      }
    };
  });

  return {
    type: 'application/ld+json',
    html: JSON.stringify(ld, stringifyReplacer)
  };
};

export const getVideoDurationStr = ({ minutes, seconds }) => {
  return `PT${minutes}M${seconds}S`;
};
export const getVideoLdObject = ({
  name,
  description,
  youtubeUrl,
  vimeoInfo: { vimeoUrl, vimeoThumbNail } = {},
  transcript,
  uploadDate,
  duration
}) => {
  const result = {};
  result['@context'] = 'https://schema.org/';
  result['@type'] = 'VideoObject';
  result.name = name;
  if (description) {
    result.description = description;
  }
  if (youtubeUrl) {
    result.contentUrl = youtubeUrl;
    result.embedUrl = generateYoutubeEmbedURL({ url: youtubeUrl });
    const thumbnail = getYoutubeThumbnailLink(youtubeUrl, false);
    result.thumbnailUrl = thumbnail ? [thumbnail] : [];
  } else if (vimeoUrl) {
    result.contentUrl = vimeoUrl;
    result.embedUrl = generateVimeoEmbedURL({ url: vimeoUrl });
    const thumbnail = vimeoThumbNail
      ? vimeoThumbNail
      : getVimeoThumbnailLinkFromURL(vimeoUrl, false);
    result.thumbnailUrl = thumbnail ? [thumbnail] : [];
  }
  if (uploadDate) {
    result.uploadDate = uploadDate;
  }
  if (duration) {
    result.duration = duration;
  }
  if (transcript) {
    result.transcript = transcript;
    result.publisher = {
      '@type': 'Organization',
      name: 'Seniorly Inc.',
      logo: {
        '@type': 'ImageObject',
        url: 'https://d1qiigpe5txw4q.cloudfront.net/assets/footer_logo.png'
      }
    };
  }
  return result;
};
